import React, {useState, useEffect} from 'react'
import {Toggle} from '@thryvlabs/maverick'
import Kp11KeapProCard from './kp-keap-cards/kp-11keap-pro-card'
import Kp11KeapMaxCard from './kp-keap-cards/kp-11keap-max-card'
import Kp11KeapUltimateCard from './kp-keap-cards/kp-11keap-ultimate-card'
import {useSelector} from 'react-redux'
import {
  selectKP11SelectCartCentersNApps,
  selectThryvSubscriptionResponse,
} from '../../../../../../app-store/kp11CartSlice'
import {
  CardPlanBodyStyle,
  CardPlanAddToCartStyle,
} from '../kp-11Link-catalog-cards-style'
import {kpSuccessIcon} from '../../../../../../assets'
import {CardPlanItemWrapStyle} from '../../../kp-11Link-reusable-components/kp-plan-radio-input-style'
import {
  DELAY_HIDE_IN_SEC,
  GENERIC_CARD_LIST,
} from '../../../../../../utils/constants'
import {
  buildCartEntity,
  countryCurrencyEnum,
  isItemAtMaxQty,
  isOneTimeItem,
} from '../../../kp-11Link-utility/kp11-utility'
import {RegularCardComponent} from '../../../../kp-card-type/regular-card'
import {formatPrice} from '../../../../../../utils/currency-format'
// import useDebouncedCallback from '../../../../../../utils/useDebouncedCallback'

const getProductPrice = (prices, country = 'US') => {
  const currency = countryCurrencyEnum[country] || ''
  const price = prices?.find(p => p.currency === currency)
  return price ? price.unitAmount : null
}

const resolveBillingType = item => {
  if (item?.productBillingType === 'Annual') return 'Annual'
  if (item?.productBillingType === 'Monthly') return 'Monthly'
  if (String(item?.code || '').includes('-annual')) return 'Annual'
  return 'Monthly'
}

const isTierBackedKeapItem = item => {
  const code = String(item?.code || '')
  return (
    code.includes('keap_pro') ||
    code.includes('keap_max') ||
    code.includes('keap_ultimate')
  )
}

const getKeapFamilyKey = code =>
  String(code || '')
    .replace(/-annual$/i, '')
    .replace(/-m2m$/i, '')

const getExistingKeapBillingType = response => {
  const subs = [
    ...(Array.isArray(response?.childSubscriptions)
      ? response.childSubscriptions
      : []),
    ...(Array.isArray(response?.parentSubscriptions)
      ? response.parentSubscriptions
      : []),
  ]
  const existingKeapSub = subs.find(sub =>
    String(sub?.code || '').includes('keap'),
  )
  if (!existingKeapSub) return null
  if (String(existingKeapSub.code).includes('-annual')) return 'Annual'
  if (String(existingKeapSub.code).includes('-m2m')) return 'Monthly'
  return null
}

const KeapTierButtons = ({
  tierItems,
  selectedTierCode,
  onTierSelect,
  selectedCountry,
  disabledItems = {},
}) => {
  return (
    <>
      {tierItems.map(catalogItem => {
        const tier = catalogItem.code
        const isActive = selectedTierCode === tier
        const tierDisabled = disabledItems[tier] || false
        const price = getProductPrice(catalogItem.currencies, selectedCountry)
        const billingType = resolveBillingType(catalogItem)
        return (
          <div
            key={tier}
            className={`card-plan-item ${tierDisabled ? 'disabled' : ''} ${
              isActive && !tierDisabled ? 'checked' : ''
            }`}
            onClick={() => !tierDisabled && onTierSelect(tier)}
          >
            <div className="radio-btn">
              <input
                type="radio"
                checked={isActive}
                disabled={tierDisabled}
                onChange={() => onTierSelect(tier)}
              />
            </div>
            <div className="item-content-wrap">
              <div className="plan-item-radio-label">{catalogItem.name}</div>
              <div className="plan-item-price">
                {formatPrice(price ?? 0, 1, '', selectedCountry)}
              </div>
              <div className="plan-item-subtext">Billed {billingType}</div>
            </div>
          </div>
        )
      })}
    </>
  )
}

const Kp11KeapCard = ({
  selectedCountry,
  groupKey,
  items,
  onSelectItem,
  disabled,
  disabledItems = {},
  addedToCart,
  onGroupCustomFieldsUpdate,
  groupCustomFields,
}) => {
  const reduxCartCentersAndApps = useSelector(selectKP11SelectCartCentersNApps)
  const thryvSubscriptionResponse = useSelector(selectThryvSubscriptionResponse)
  const centers = reduxCartCentersAndApps?.centers?.map(item => item.code) || []
  const apps = reduxCartCentersAndApps?.apps?.map(item => item.code) || []
  const oneTimeFee =
    reduxCartCentersAndApps?.oneTimeFees?.map(item => item.code) || []
  const codeList = [...centers, ...apps, ...oneTimeFee]

  // Find the selected Keap tier code from the cart
  const tierItemsInfo = items.filter(isTierBackedKeapItem)
  const standaloneKeapItems = items.filter(item => !isTierBackedKeapItem(item))
  const selectedKeapTierInCart = tierItemsInfo.find(item =>
    codeList.includes(item.code),
  )?.code

  const [selectedTierCode, setSelectedTierCode] = useState(null)
  const [isAnnualSelected, setIsAnnualSelected] = useState(false)

  const existingKeapBillingType = getExistingKeapBillingType(
    thryvSubscriptionResponse,
  )
  const isBillingLocked = !!existingKeapBillingType

  const tierVariantsByFamily = tierItemsInfo.reduce((acc, item) => {
    const familyKey = getKeapFamilyKey(item.code)
    if (!acc[familyKey]) {
      acc[familyKey] = {}
    }
    acc[familyKey][resolveBillingType(item)] = item
    return acc
  }, {})

  const hasMonthlyTier = Object.values(tierVariantsByFamily).some(
    variants => !!variants.Monthly,
  )
  const hasAnnualTier = Object.values(tierVariantsByFamily).some(
    variants => !!variants.Annual,
  )
  const canShowBillingToggle =
    hasMonthlyTier && hasAnnualTier && !isBillingLocked

  const effectiveBillingType = isBillingLocked
    ? existingKeapBillingType
    : isAnnualSelected
    ? 'Annual'
    : 'Monthly'

  const visibleTierItems = Object.values(tierVariantsByFamily)
    .map(variants => variants[effectiveBillingType])
    .filter(Boolean)
    .sort(
      (a, b) =>
        (a.productSequence ?? Infinity) - (b.productSequence ?? Infinity),
    )

  const visibleStandaloneKeapItems = standaloneKeapItems
    .filter(item => {
      if (isOneTimeItem(item?.code)) return true
      const billingType = resolveBillingType(item)
      if (effectiveBillingType === 'Annual') {
        return billingType === 'Annual'
      }
      return billingType !== 'Annual'
    })
    .sort(
      (a, b) =>
        (a.productSequence ?? Infinity) - (b.productSequence ?? Infinity),
    )

  // Sync selected tier from cart so UI matches cart (e.g. after adding Pro, tier "Pro" stays selected)
  useEffect(() => {
    if (selectedKeapTierInCart) {
      setSelectedTierCode(selectedKeapTierInCart)
    }
  }, [selectedKeapTierInCart])

  useEffect(() => {
    if (!selectedKeapTierInCart) return
    const selectedItem = tierItemsInfo.find(
      i => i.code === selectedKeapTierInCart,
    )
    if (!selectedItem) return
    setIsAnnualSelected(resolveBillingType(selectedItem) === 'Annual')
  }, [selectedKeapTierInCart, tierItemsInfo])

  useEffect(() => {
    if (selectedKeapTierInCart) return
    if (visibleTierItems.some(item => item.code === selectedTierCode)) return
    setSelectedTierCode(visibleTierItems?.[0]?.code || null)
  }, [visibleTierItems, selectedTierCode, selectedKeapTierInCart])

  // Handle tier button click — toggle off if same, swap if different
  const handleTierSelect = tierCode => {
    setSelectedTierCode(tierCode)
  }

  const selectedTierItem = tierItemsInfo.find(i => i.code === selectedTierCode)

  const renderSelectedKeapCard = item => {
    if (!item) return null
    const commonProps = {
      name: item.name,
      price: getProductPrice(item.currencies, selectedCountry),
      isChecked: selectedKeapTierInCart === item.code,
      isDisabled:
        disabledItems[item.code] ||
        isItemAtMaxQty({
          cartCentersAndApps: reduxCartCentersAndApps,
          item,
        }),
      handleCardAction: () => {
        const updatedItem = buildCartEntity({
          kind: 'centers',
          source: {
            ...item,
            groupKey,
            section: items[0]?.section,
          },
          autoAdd: 'keap-required-entitlements',
        })
        onSelectItem(updatedItem)
      },
      groupKey,
      itemCode: item.code,
      entitlements: item.entitlements,
      onGroupCustomFieldsUpdate,
      groupCustomFields,
    }

    if (String(item.code).includes('keap_pro')) {
      return <Kp11KeapProCard {...commonProps} />
    }
    if (String(item.code).includes('keap_max')) {
      return <Kp11KeapMaxCard {...commonProps} />
    }
    if (String(item.code).includes('keap_ultimate')) {
      return <Kp11KeapUltimateCard {...commonProps} />
    }
    return (
      <RegularCardComponent
        groupKey={groupKey}
        isChecked={selectedKeapTierInCart === item.code}
        handleCardAction={commonProps.handleCardAction}
        isDisabled={commonProps.isDisabled}
        country={selectedCountry}
        cardDetails={{
          ...item,
          billingType: `Billed ${resolveBillingType(item)}`,
        }}
      />
    )
  }

  const [add2CartBannerStatus, setAdd2BannerStatus] = useState(addedToCart)
  useEffect(() => {
    if (addedToCart) {
      setAdd2BannerStatus(true)
      setTimeout(() => {
        setAdd2BannerStatus(false)
      }, DELAY_HIDE_IN_SEC * 1000)
    }
  }, [addedToCart])

  return (
    <>
      <CardPlanBodyStyle>
        <CardPlanItemWrapStyle wrap={true} column={true}>
          {canShowBillingToggle && (
            <div style={{marginBottom: '12px', paddingLeft: '8px'}}>
              <Toggle
                label="Annual"
                toggleSize="normal"
                onChange={setIsAnnualSelected}
                toggled={isAnnualSelected}
                customSelectedColor="#16A085"
                customUnselectedColor="#808080"
              />
            </div>
          )}

          {visibleTierItems.length > 0 && (
            <KeapTierButtons
              tierItems={visibleTierItems}
              selectedTierCode={selectedTierCode}
              onTierSelect={handleTierSelect}
              selectedCountry={selectedCountry}
              disabledItems={disabledItems}
            />
          )}

          {renderSelectedKeapCard(selectedTierItem)}

          {visibleStandaloneKeapItems.map(item => {
            if (
              GENERIC_CARD_LIST.includes(item.code) ||
              String(item?.code || '').startsWith('keap')
            ) {
              return (
                <RegularCardComponent
                  key={item.id}
                  groupKey={groupKey}
                  isChecked={codeList.includes(item.code)}
                  hideRadio={true}
                  handleCardAction={() => {
                    if (!codeList.includes(item.code)) {
                      const updatedItem = buildCartEntity({
                        kind: 'item',
                        source: {
                          ...item,
                          groupKey,
                          section: item?.section,
                        },
                      })
                      onSelectItem(updatedItem)
                    }
                  }}
                  isDisabled={
                    disabledItems[item.code] ||
                    isItemAtMaxQty({
                      cartCentersAndApps: reduxCartCentersAndApps,
                      item,
                    })
                  }
                  country={selectedCountry}
                  cardDetails={{
                    ...item,
                    billingType: `Billed ${resolveBillingType(item)}`,
                  }}
                />
              )
            }
            return null
          })}
        </CardPlanItemWrapStyle>
        {add2CartBannerStatus && (
          <CardPlanAddToCartStyle>
            <img src={kpSuccessIcon} alt="success icon" />
            <span>Added to cart</span>
          </CardPlanAddToCartStyle>
        )}
      </CardPlanBodyStyle>
    </>
  )
}

export default Kp11KeapCard
